import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import PatientStipendWorklistApi from 'api/patient/PatientStipendWorklistApi';
import { isEmpty } from 'lodash/lang';

import { useEuiFiltersState, withEuiFiltersProvider } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { FRS_READY } from 'components/eui/EuiFiltersContext/reducer';

import { muiDataGridLocaleText } from '../../../../services/i18n/mui';
import useDataGridOnStateChange from '../../../eui/EuiFiltersContext/hooks/useDataGridOnStateChange';

import FiltersSection from './FiltersSection/FiltersSection';
import { SelectedItemsMenu } from './SelectedItemsMenu/SelectedItemsMenu';
import { STP_DENIED, STP_IN_REVIEW } from './constants';
import useDataGrid from './useDataGrid';

import './PatientStipendWorklist.scss';

function PatientStipendWorklist() {
  const onceLoaded = useRef(false);
  const alreadyLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const { schema, statuses, ssu, dataGridState } = useEuiFiltersState();
  const onStateChange = useDataGridOnStateChange();
  const [columns, gridData, setGridData] = useDataGrid();
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set([])
  });

  const selectedIds = useMemo(() => {
    return Array.from(rowSelectionModel.ids);
  }, [rowSelectionModel]);

  const requestObject = useMemo(
    function() {
      if (schema?.status !== FRS_READY || isEmpty(statuses?.value) || isEmpty(ssu)) {
        return;
      }
      const { studySites, selectedStudyMap, selectedSiteMap } = ssu;
      const ssuIds = studySites.reduce(function(accumulator, { id, study, site }) {
        if (selectedStudyMap[study.id] && selectedSiteMap[site.id]) {
          accumulator.push(id);
        }
        return accumulator;
      }, []);
      alreadyLoaded.current = false;
      return {
        statuses: statuses.value.map(status => status.id),
        ssuIds
      };
    },
    [schema, statuses, ssu]
  );

  const loadTableData = useCallback(
    function(request) {
      setLoading(true);
      PatientStipendWorklistApi.getRows(request)
        .then(({ data }) => {
          setGridData(data);
          setShowCheckboxes(
            ssu?.selectedStudies?.length === 1 &&
              data.some(row => [STP_IN_REVIEW, STP_DENIED].includes(row.status) && row.stipendOversight)
          );
          alreadyLoaded.current = true;
        })
        .finally(() => setLoading(false));
    },
    [setGridData, ssu]
  );

  const onApply = useCallback(
    function() {
      if (requestObject) {
        loadTableData(requestObject);
      }
    },
    [loadTableData, requestObject]
  );

  useEffect(
    function() {
      if (onceLoaded.current || !requestObject) {
        return;
      }
      loadTableData(requestObject);
      onceLoaded.current = true;
    },
    [loadTableData, requestObject]
  );

  useEffect(
    function() {
      if (!showCheckboxes) {
        setRowSelectionModel({ type: 'include', ids: new Set([]) });
      }
    },
    [showCheckboxes]
  );

  const applyDisabled = alreadyLoaded.current || loading;
  const dataGridStateReady = schema?.dataGridState?.status === FRS_READY;

  const isRowSelectable = useCallback(({ row }) => {
    return [STP_IN_REVIEW, STP_DENIED].includes(row.status) && row.stipendOversight;
  }, []);

  return (
    <div className="eui-patient-stipend-worklist">
      <FiltersSection gridData={gridData} onApply={onApply} applyDisabled={applyDisabled} />
      <SelectedItemsMenu
        gridData={gridData}
        setGridData={setGridData}
        selectedIds={selectedIds}
        setRowSelectionModel={setRowSelectionModel}
      />
      {dataGridStateReady && (
        <DataGridPremium
          initialState={dataGridState}
          onStateChange={onStateChange}
          slotProps={{
            toolbar: {
              printOptions: { disableToolbarButton: true },
              excelOptions: { disableToolbarButton: true },
              quickFilterProps: {
                debounceMs: 500
              }
            }
          }}
          loading={loading}
          rowHeight={50}
          rows={gridData}
          columns={columns}
          checkboxSelection={showCheckboxes}
          isRowSelectable={isRowSelectable}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={function(newRowSelectionModel) {
            setRowSelectionModel(newRowSelectionModel);
          }}
          disableRowSelectionOnClick
          localeText={muiDataGridLocaleText({ noRowsLabel: 'No results to display' })}
          disableRowGrouping
          disableAggregation
          disablePivoting
          showToolbar
          disableRowSelectionExcludeModel
        />
      )}
    </div>
  );
}

export default withEuiFiltersProvider(PatientStipendWorklist, { cachePrefix: 'PSWF' });
